import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, MenuItem, Select, Stack, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { useParams } from 'react-router-dom';
import GridSection from 'components/_layouts/GridSection';
import QuickSearch from 'components/_atoms/QuickSearch';
import PhxBadge from '_lex/_atoms/PhxBadge';
import PageToolbar from '_lex/_atoms/PageToolbar';
import useUrlParams from 'hooks/useUrlParams';
import { useEisScoredFields } from '../../_data_sources/useEisScoredFields';
import { useEisRuleDetailsContext } from '../../context';
import type { EisScoredFieldItem, EisScoringStatus } from '../../_types';

export type ScoredFieldsParams = {
  search?: string | null;
  dataType?: string | null;
  status?: EisScoringStatus | null;
  sort?: string | null;
};

const defaultParams: ScoredFieldsParams = {
  search: null,
  dataType: null,
  status: null,
  sort: null,
};

const SCORED_FIELDS_DATA_TYPE_OPTIONS: { key: string; labelKey: string }[] = [
  { key: '', labelKey: 'performanceReporting.eis.details.scoredFields.filters.all' },
  { key: 'MultiSelect', labelKey: 'performanceReporting.eis.details.scoredFields.fieldType.MultiSelect' },
  { key: 'SingleSelect', labelKey: 'performanceReporting.eis.details.scoredFields.fieldType.SingleSelect' },
  { key: 'Number', labelKey: 'performanceReporting.eis.details.scoredFields.fieldType.Number' },
  { key: 'Boolean', labelKey: 'performanceReporting.eis.details.scoredFields.fieldType.Boolean' },
];

const SCORED_FIELDS_STATUS_OPTIONS: { key: EisScoringStatus | ''; labelKey: string }[] = [
  { key: '', labelKey: 'performanceReporting.eis.details.scoredFields.filters.all' },
  { key: 'NotScored', labelKey: 'performanceReporting.eis.details.scoredFields.status.notScored' },
  { key: 'PartiallyScored', labelKey: 'performanceReporting.eis.details.scoredFields.status.partiallyScored' },
  { key: 'Scored', labelKey: 'performanceReporting.eis.details.scoredFields.status.scored' },
];

function getScoredAnswersText(item: EisScoredFieldItem): string {
  const scores = item.scores ?? [];
  const total = scores.length;
  const withPoints = scores.filter((s) => s.points != null && s.points !== undefined).length;
  return `${withPoints} of ${total}`;
}

export default function ScoredFields() {
  const { id } = useParams<{ id: string }>();
  const ruleId = id ? Number(id) : 0;
  const { rule, isLoading: isRuleLoading } = useEisRuleDetailsContext();
  const [params, setParams] = useUrlParams(defaultParams);
  const [sortModel, setSortModel] = useState<{ field: string; sort: 'asc' | 'desc' }[]>(() => {
    const s = params.sort;
    if (!s) return [];
    return s.split(',').map((entry: string) => {
      const isDesc = entry.startsWith('-');
      return { field: isDesc ? entry.slice(1) : entry, sort: isDesc ? 'desc' : ('asc' as const) };
    });
  });

  const queryParams = useMemo(
    () => ({
      ruleVersionId: rule?.versionId,
      search: params.search || undefined,
      dataType: params.dataType || undefined,
      status: params.status || undefined,
      sort: params.sort || undefined,
    }),
    [rule?.versionId, params.search, params.dataType, params.status, params.sort],
  );

  const scoredFieldsQuery = useEisScoredFields(
    ruleId,
    queryParams,
    { enabled: !!ruleId && !!rule?.versionId },
    (err) => {
      console.error('Error fetching EIS scored fields:', err);
    },
  );

  const allItems = scoredFieldsQuery.data?.pages.flatMap((p) => p.content) ?? [];
  const totalCount = scoredFieldsQuery.data?.pages[0]?.count ?? 0;

  const updateParams = useCallback(
    (updated: Partial<ScoredFieldsParams>) => {
      setParams((prev: ScoredFieldsParams) => {
        const next = { ...prev, ...updated };
        document.body.dispatchEvent(new Event('params-changed'));
        return next;
      });
    },
    [setParams],
  );

  const onSearchChange = useCallback((val: string) => updateParams({ search: val?.trim() || null }), [updateParams]);

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    setSortModel([]);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const isFiltered = !!(params.search || params.dataType || params.status || params.sort);

  const handleSortModelChange = useCallback(
    (model: { field: string; sort: 'asc' | 'desc' }[]) => {
      setSortModel(model);
      if (model.length > 0) {
        const sort = model.map(({ field, sort }) => (sort === 'desc' ? `-${field}` : field)).join(',');
        updateParams({ sort });
      } else {
        updateParams({ sort: null });
      }
    },
    [updateParams],
  );

  const columns = useMemo(
    () => ({
      defaultLabel: {
        header: t('performanceReporting.eis.details.scoredFields.columns.defaultLabel'),
        flex: 2,
        sortable: true,
        valueGetter: (_: unknown, row: EisScoredFieldItem) => row?.field?.label ?? '',
        renderer: (params: { row: EisScoredFieldItem }) => (
          <Typography variant='body1'>{params.row?.field?.label ?? '--'}</Typography>
        ),
      },
      fieldType: {
        header: t('performanceReporting.eis.details.scoredFields.columns.fieldType'),
        flex: 1.5,
        sortable: true,
        valueGetter: (_: unknown, row: EisScoredFieldItem) => row?.field?.type ?? '',
        renderer: (params: { row: EisScoredFieldItem }) => {
          const type = params.row?.field?.type;
          const label =
            type && t(`performanceReporting.eis.details.scoredFields.fieldType.${type}`)
              ? t(`performanceReporting.eis.details.scoredFields.fieldType.${type}`)
              : (type ?? '--');
          return <Typography variant='body1'>{label}</Typography>;
        },
      },
      templateSource: {
        header: t('performanceReporting.eis.details.scoredFields.columns.templateSource'),
        flex: 2,
        sortable: true,
        valueGetter: (_: unknown, row: EisScoredFieldItem) => row?.templateSource?.name ?? '',
        renderer: (params: { row: EisScoredFieldItem }) => (
          <Typography variant='body1'>{params.row?.templateSource?.name ?? '--'}</Typography>
        ),
      },
      scoredAnswers: {
        header: t('performanceReporting.eis.details.scoredFields.columns.scoredAnswers'),
        flex: 1,
        sortable: false,
        valueGetter: (_: unknown, row: EisScoredFieldItem) => getScoredAnswersText(row),
        renderer: (params: { row: EisScoredFieldItem }) => (
          <Typography variant='body1'>{getScoredAnswersText(params.row)}</Typography>
        ),
      },
      status: {
        header: t('performanceReporting.eis.details.scoredFields.columns.status'),
        flex: 1.2,
        sortable: true,
        valueGetter: (_: unknown, row: EisScoredFieldItem) => row?.status ?? 'NotScored',
        renderer: (params: { row: EisScoredFieldItem }) => {
          const status = params.row?.status ?? 'NotScored';
          const color: 'success' | 'warning' | 'default' =
            status === 'Scored' ? 'success' : status === 'PartiallyScored' ? 'warning' : 'default';
          const label =
            status === 'Scored'
              ? t('performanceReporting.eis.details.scoredFields.status.scored')
              : status === 'PartiallyScored'
                ? t('performanceReporting.eis.details.scoredFields.status.partiallyScored')
                : t('performanceReporting.eis.details.scoredFields.status.notScored');
          return (
            <PhxBadge
              color={color}
              size='sm'
            >
              {label}
            </PhxBadge>
          );
        },
      },
    }),
    [t],
  );

  const isLoading = isRuleLoading || scoredFieldsQuery.isLoading;
  const isError = scoredFieldsQuery.isError;

  return (
    <Stack
      spacing={4}
      sx={{ minHeight: 0 }}
    >
      <PageToolbar
        leftItems={
          <Stack
            direction='row'
            spacing={3}
            alignItems='center'
          >
            <QuickSearch
              id='scored_fields_search'
              initialValue={params.search || ''}
              onChange={onSearchChange}
              placeholder={t('performanceReporting.eis.details.scoredFields.search.placeholder')}
            />
            <Select
              id='data_type'
              value={params.dataType || ''}
              size='small'
              displayEmpty
              onChange={(e) => updateParams({ dataType: (e.target.value as string) || null })}
            >
              {SCORED_FIELDS_DATA_TYPE_OPTIONS.map((x) => (
                <MenuItem
                  key={`dataType_${x.key || 'all'}`}
                  value={x.key}
                >{`${t('performanceReporting.eis.details.scoredFields.filters.dataType')}: ${t(x.labelKey)}`}</MenuItem>
              ))}
            </Select>
            <Select
              id='status'
              value={params.status || ''}
              size='small'
              displayEmpty
              onChange={(e) => updateParams({ status: (e.target.value as EisScoringStatus) || null })}
            >
              {SCORED_FIELDS_STATUS_OPTIONS.map((x) => (
                <MenuItem
                  key={`status_${x.key || 'all'}`}
                  value={x.key}
                >{`${t('performanceReporting.eis.details.scoredFields.filters.status')}: ${t(x.labelKey)}`}</MenuItem>
              ))}
            </Select>
          </Stack>
        }
        rightItems={
          isFiltered ? (
            <Button
              color='neutral'
              size='small'
              onClick={clearParams}
            >
              {t('performanceReporting.eis.details.scoredFields.actions.clearFilters')}
            </Button>
          ) : null
        }
      />
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
          {t('performanceReporting.eis.details.scoredFields.errorLoading')}
        </Box>
      ) : allItems.length === 0 ? (
        <Stack
          spacing={3}
          alignItems='center'
          sx={{ p: 4 }}
        >
          <Box
            component='img'
            src='/admin/img/performance-reporting/empty-eis.png'
            alt={t('performanceReporting.eis.details.scoredFields.emptyState.title')}
          />
          <Typography
            variant='body1'
            sx={{ fontWeight: 600 }}
          >
            {t('performanceReporting.eis.details.scoredFields.emptyState.title')}
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {t('performanceReporting.eis.details.scoredFields.emptyState.subtitle')}
          </Typography>
        </Stack>
      ) : (
        <Box sx={{ minHeight: 400 }}>
          <GridSection
            columns={columns}
            data={allItems}
            query={scoredFieldsQuery}
            totalCount={totalCount}
            sorting={sortModel}
            onSortModelChange={handleSortModelChange}
            gridKey={`scored-fields-${ruleId}-${params.search ?? ''}-${params.dataType ?? ''}-${params.status ?? ''}-${params.sort ?? ''}`}
            gridProps={{
              getRowId: (row: EisScoredFieldItem) => row.id,
              sortingMode: 'server',
            }}
          />
        </Box>
      )}
    </Stack>
  );
}
